import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, GraduationCap, Calendar, Clock, CheckCircle } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from '../../database/firebase';

const CohortPerformance = () => {
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(
    () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const loadCohorts = async () => {
      setIsLoading(true);
      try {
        const snapshot = await getDocs(collection(firestore, 'clients'));
        const clients = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const cohortMap = {};
        clients.forEach(client => {
          const group = client.corhotGroup || 'Unassigned';
          if (!cohortMap[group]) {
            cohortMap[group] = {
              id: group,
              name: `Cohort ${group}`,
              clients: [],
              totalStudents: 0,
              activeStudents: 0,
              completedStudents: 0,
              averageAttendance: 0,
              completionRate: 0,
              averageHealthScore: 0,
              status: 'active',
              modules: [],
            };
          }
          cohortMap[group].clients.push(client);
        });
        const cohortList = Object.values(cohortMap).map(cohort => {
          const total = cohort.clients.length;
          const active = cohort.clients.filter(c => c.status === 'active').length;
          const completed = cohort.clients.filter(c => c.status === 'completed').length;
          const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
          return {
            ...cohort,
            totalStudents: total,
            activeStudents: active,
            completedStudents: completed,
            completionRate,
          };
        });
        setCohorts(cohortList);
        setSelectedCohort(cohortList[0] || null);
      } catch (err) {
        setCohorts([]);
        setSelectedCohort(null);
      } finally {
        setIsLoading(false);
      }
    };
    loadCohorts();
    const darkListener = e => setDarkMode(e.matches);
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', darkListener);
    return () =>
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', darkListener);
  }, []);

  const statusConfig = {
    active: {
      bg: 'bg-accent/10',
      text: 'text-accent',
      icon: Clock,
    },
    completed: {
      bg: 'bg-success/10',
      text: 'text-success',
      icon: CheckCircle,
    },
    upcoming: {
      bg: 'bg-primary/10',
      text: 'text-primary',
      icon: Calendar,
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  if (isLoading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
          {[...Array(3)].map((_, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              className="rounded-xl border border-accent/20 bg-bg-card p-6 shadow-sm dark:border-accent/40 dark:bg-nav"
            >
              <div className="animate-pulse">
                <div className="mb-4 h-6 w-32 rounded bg-cyan-100 dark:bg-cyan-900"></div>
                <div className="space-y-3">
                  <div className="h-4 w-24 rounded bg-cyan-100 dark:bg-cyan-900"></div>
                  <div className="h-8 w-16 rounded bg-cyan-100 dark:bg-cyan-900"></div>
                  <div className="h-4 w-20 rounded bg-cyan-100 dark:bg-cyan-900"></div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="rounded-xl border border-cyan-100 bg-white p-4 shadow-sm dark:border-cyan-900 dark:bg-neutral-900 md:p-6">
          <div className="animate-pulse">
            <div className="mb-6 h-6 w-48 rounded bg-cyan-100 dark:bg-cyan-900"></div>
            <div className="space-y-4">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="h-4 w-32 rounded bg-cyan-100 dark:bg-cyan-900"></div>
                  <div className="h-4 w-24 rounded bg-cyan-100 dark:bg-cyan-900"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-4 sm:space-y-6"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-accent p-2 dark:bg-accent/80">
            <GraduationCap className="h-5 w-5 text-bg-card dark:text-bg-main" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Cohort Performance
            </h3>
            <p className="text-sm text-gray-900/60">Track cohort progress and business health</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
        {cohorts.map(cohort => {
          const status = statusConfig[cohort.status];
          const StatusIcon = status.icon;
          return (
            <motion.div
              key={cohort.id}
              variants={cardVariants}
              whileHover={{ y: -4, scale: 1.02 }}
              className={`cursor-pointer rounded-xl border border-accent/20 bg-bg-card p-4 shadow-sm transition-all duration-300 dark:border-accent/40 dark:bg-nav md:p-6 ${
                selectedCohort?.id === cohort.id ? 'ring-2 ring-accent dark:ring-accent/60' : ''
              }`}
              onClick={() => setSelectedCohort(cohort)}
            >
              <div className="mb-4 flex items-center justify-between">
                <div className={`rounded-lg p-2 ${status.bg}`}>
                  {' '}
                  <StatusIcon className={`h-5 w-5 ${status.text}`} />{' '}
                </div>
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium ${status.bg} ${status.text}`}
                >
                  {cohort.status}
                </span>
              </div>
              <h4 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100 md:text-lg">
                {cohort.name}
              </h4>
              <div className="space-y-2 md:space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-900/60 md:text-sm">Clients</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {cohort.activeStudents || cohort.completedStudents}/{cohort.totalStudents}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-900/60 md:text-sm">Completion</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {cohort.completionRate}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-900/60 md:text-sm">Health Score</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {cohort.averageHealthScore}%
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-accent/10 dark:bg-accent/90">
                  <div
                    className="h-2 rounded-full bg-primary transition-all duration-300"
                    style={{ width: `${cohort.completionRate}%` }}
                  ></div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      {selectedCohort && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-accent/20 bg-bg-card p-4 shadow-sm dark:border-accent/40 dark:bg-nav md:p-6"
        >
          <div className="mb-4 flex flex-col gap-3 md:mb-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 md:text-xl">
                {selectedCohort.name}
              </h3>
              <p className="text-sm text-gray-900/60">
                {new Date(selectedCohort.startDate).toLocaleDateString()} -{' '}
                {new Date(selectedCohort.endDate).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-xl font-bold text-gray-900 md:text-2xl">
                  {selectedCohort.averageAttendance}%
                </p>
                <p className="text-sm text-gray-900/60">Attendance</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-gray-900 md:text-2xl">
                  {selectedCohort.completionRate}%
                </p>
                <p className="text-sm text-gray-900/60">Completion</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-gray-900 md:text-2xl">
                  {selectedCohort.averageHealthScore}%
                </p>
                <p className="text-sm text-gray-900/60">Health Score</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 md:text-lg">
              Module Progress
            </h4>
            {selectedCohort.modules.map((module, index) => (
              <motion.div
                key={module.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="rounded-lg bg-accent/5 p-3 dark:bg-accent/90 md:p-4"
              >
                <div className="mb-2 flex items-center justify-between">
                  <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100 md:text-base">
                    {module.name}
                  </h5>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-gray-900/60 md:text-sm">
                      Progress: {module.progress}%
                    </span>
                    <span className="text-xs text-gray-900/60 md:text-sm">
                      Health: {module.healthScore}%
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-2 w-full rounded-full bg-accent/10 dark:bg-accent/90">
                    <div
                      className="h-2 rounded-full bg-primary transition-all duration-500"
                      style={{ width: `${module.progress}%` }}
                    ></div>
                  </div>
                  <div className="h-2 w-full rounded-full bg-success/10 dark:bg-success/90">
                    <div
                      className="h-2 rounded-full bg-success transition-all duration-500"
                      style={{ width: `${module.healthScore}%` }}
                    ></div>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between text-[10px] text-gray-900/60 md:text-xs">
                  <span>Progress</span>
                  <span>Business Health</span>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-6 border-t border-accent/20 pt-6 dark:border-accent/40">
            <h4 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100 md:text-lg">
              Student Distribution
            </h4>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
              {['total', 'completed', 'active'].map((type, idx) => {
                let clientsToShow = [];
                if (type === 'total') {
                  clientsToShow = selectedCohort.clients;
                } else if (type === 'completed') {
                  clientsToShow = selectedCohort.clients.filter(c => c.status === 'completed');
                } else if (type === 'active') {
                  clientsToShow = selectedCohort.clients.filter(c => c.status === 'active');
                }
                const cardConfig = [
                  {
                    bg: 'bg-accent/10 dark:bg-accent/90',
                    icon: <Users className="mx-auto mb-2 h-8 w-8 text-accent" />,
                    value: selectedCohort.totalStudents,
                    label: 'Total Enrolled',
                  },
                  {
                    bg: 'bg-success/10 dark:bg-success/90',
                    icon: <CheckCircle className="mx-auto mb-2 h-8 w-8 text-success" />,
                    value: selectedCohort.completedStudents,
                    label: 'Completed',
                  },
                  {
                    bg: 'bg-primary/10 dark:bg-primary/90',
                    icon: <Clock className="mx-auto mb-2 h-8 w-8 text-primary" />,
                    value: selectedCohort.activeStudents,
                    label: 'Active',
                  },
                ][idx];
                return (
                  <div
                    key={type}
                    className={`group relative cursor-pointer rounded-lg p-3 text-center md:p-4 ${cardConfig.bg}`}
                  >
                    {cardConfig.icon}
                    <p className="text-xl font-bold text-gray-900 md:text-2xl">
                      {cardConfig.value}
                    </p>
                    <p className="text-xs text-gray-900/60 md:text-sm">{cardConfig.label}</p>
                    <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-64 -translate-x-1/2 rounded-lg bg-white p-4 text-left text-xs opacity-0 shadow-lg ring-1 ring-black/10 transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100 dark:bg-neutral-900 dark:text-gray-100">
                      <div className="mb-2 text-xs font-semibold md:text-sm">Clients</div>
                      {clientsToShow.length === 0 ? (
                        <div className="text-gray-500">No clients</div>
                      ) : (
                        <ul className="max-h-40 space-y-1 overflow-y-auto pr-1">
                          {clientsToShow.map(client => (
                            <li
                              key={client.id}
                              className="flex flex-col border-b border-gray-100 pb-1 last:border-b-0 dark:border-gray-800"
                            >
                              <span className="text-xs font-medium md:text-sm">
                                {client.organization || 'N/A'}
                              </span>
                              <span className="text-[10px] text-gray-500 md:text-xs">
                                {client.email}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setDarkMode(dm => !dm)}
          className="rounded-full bg-primary p-3 text-bg-card shadow-lg transition-colors hover:bg-primary/90 dark:bg-primary dark:hover:bg-primary/80"
          aria-label="Toggle dark mode"
        >
          {darkMode ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.66 5.66l-.71-.71M4.05 4.05l-.71-.71M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12.79A9 9 0 1111.21 3a7 7 0 109.79 9.79z"
              />
            </svg>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default CohortPerformance;
